import type { GroupEntity, UserEntity, PhoneBooksEntity } from "~/utils/receptionDB.types";
import { hiraganaToKatakana } from "~/utils/textNormalizationUtils";
import type { VisitorConfirmationNavigationState } from "~/reception/VisitorConfirmationPage";

// 検索結果の判別型
export type SearchResult =
  | { kind: "groups"; items: GroupEntity[] }
  | { kind: "users"; items: UserEntity[] }
  | { kind: "none" };

export type VisitorSelectionNavigationState = {
  companyName?: string;
  visitorName?: string;
  department?: string;
  personInCharge?: string;
  personInChargeKana?: string;
};

// namesフィールドを全言語コード横断で部分一致チェックする。
// スペース区切りトークンが全て名前に含まれていれば一致とみなす。
const matchesName = (names: { languageCode: string; name: string }[], query: string): boolean => {
  const tokens = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0);
  return names.some((n) => {
    const lower = n.name.toLowerCase();
    return tokens.every((token) => lower.includes(token));
  });
};

// カナ表記をカタカナへ統一し、ひらがな/全半角スペースの揺れを吸収する
const normalizeKana = (text: string): string => hiraganaToKatakana(text).replace(/\s/g, "");

// kanaNameをカタカナ正規化したうえで部分一致チェックする。
const matchesKanaName = (kanaName: string | null, query: string): boolean => {
  if (!kanaName) return false;
  return normalizeKana(kanaName).includes(query);
};

// rootGroupIdを起点に再帰的に子孫グループIDのSetを返す
const getDescendantGroupIds = (rootGroupId: number, groupList: GroupEntity[]): Set<number> => {
  const result = new Set<number>();
  const queue = [rootGroupId];
  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined) break;
    result.add(current);
    for (const g of groupList) {
      if (g.parentGroupId === current) {
        queue.push(g.groupId);
      }
    }
  }
  return result;
};

// groupPatternに一致するグループの子孫グループIDのSetを返す
const getMatchedDescendantIds = (groupPattern: string, groupList: GroupEntity[]): Set<number> => {
  const matchedGroups = groupList.filter((g) => matchesName(g.names, groupPattern));
  const descendantIds = new Set<number>();
  for (const g of matchedGroups) {
    for (const id of getDescendantGroupIds(g.groupId, groupList)) {
      descendantIds.add(id);
    }
  }
  return descendantIds;
};

// companyIdが有効な場合、そのrootGroupIdの子孫グループ（root自身を除く）に絞り込んだリストを返す
const getScopedGroupList = (
  groupList: GroupEntity[],
  rootGroupId: number | null
): GroupEntity[] => {
  if (rootGroupId === null || rootGroupId === 0) return groupList;
  const scopeIds = getDescendantGroupIds(rootGroupId, groupList);
  // rootGroupId 自身は除外し、直下の子孫のみをスコープとする
  scopeIds.delete(rootGroupId);
  return groupList.filter((g) => scopeIds.has(g.groupId));
};

/**
 * グループ選択時の visitor-confirmation 遷移用 state を生成する。
 *
 * @param groupId - 選択されたグループの ID（PhoneBooksEntity.groupList[].groupId）
 * @param groupName - AI が抽出した部署名。null の場合は state から省略される
 * @param visitorName - 来訪者氏名。null の場合は state から省略される
 * @param companyName - 来訪者の会社名。null の場合は state から省略される
 */
export const buildGroupConfirmState = (
  groupId: number,
  groupName: string | null,
  visitorName: string | null,
  companyName: string | null
): VisitorConfirmationNavigationState => {
  return {
    targetGroupId: groupId,
    department: groupName ?? undefined,
    visitorName: visitorName ?? undefined,
    companyName: companyName ?? undefined,
  };
};

/**
 * ユーザー選択時の visitor-confirmation 遷移用 state を生成する。
 *
 * @param user - 選択された UserEntity
 * @param parentGroupId - user が所属するグループの groupId（= user.groupId）。グループが特定できない場合は undefined
 * @param groupName - AI が抽出した部署名。null の場合は state から省略される
 * @param userName - AI が抽出した担当者名（漢字表記）。null の場合は state から省略される
 * @param visitorName - 来訪者氏名。null の場合は state から省略される
 * @param companyName - 来訪者の会社名。null の場合は state から省略される
 */
export const buildUserConfirmState = (
  user: UserEntity,
  parentGroupId: number | undefined,
  groupName: string | null,
  userName: string | null,
  visitorName: string | null,
  companyName: string | null
): VisitorConfirmationNavigationState => {
  return {
    targetGroupId: parentGroupId,
    targetGroupUserId: user.groupUserId,
    department: groupName ?? undefined,
    personInCharge: userName ?? undefined,
    visitorName: visitorName ?? undefined,
    companyName: companyName ?? undefined,
  };
};

/**
 * groupName・userName に基づいて PhoneBooksEntity を検索し、結果を SearchResult として返す。
 *
 * - groupName のみ指定 → rootGroupId スコープ内で isRecept=true のグループを名前で絞り込む
 * - userName のみ指定 → rootGroupId スコープ内のユーザーをカナ（kanaName）で絞り込む
 * - 両方指定 → groupName に一致するグループの子孫グループに属するユーザーをカナで絞り込む
 * - どちらも空 → `{ kind: "none" }` を返す
 *
 * 担当者の照合は音声認識による漢字表記の揺れを避けるため、userNameKana（AI 抽出のカナ読み）と
 * UserEntity.kanaName を突き合わせて行う。userNameKana が null または空の場合は一致なしとする。
 *
 * @param entity - IndexedDB から取得した PhoneBooksEntity
 * @param groupName - 検索する部署名（AI 抽出値）。null または空文字の場合は部署絞り込みなし
 * @param userName - 検索する担当者名・漢字表記（AI 抽出値）。null または空文字の場合はユーザー絞り込みなし
 * @param userNameKana - 担当者名のカナ読み（AI 抽出値）。照合キーとして使用する
 * @param rootGroupId - 検索スコープの起点となるグループ ID。null または 0 の場合は全グループが対象
 */
export const searchEntities = (
  entity: PhoneBooksEntity,
  groupName: string | null,
  userName: string | null,
  userNameKana: string | null,
  rootGroupId: number | null
): SearchResult => {
  const normalizedGroupName = groupName?.trim() ?? "";
  const normalizedUserName = userName?.trim() ?? "";
  const normalizedUserKana = normalizeKana(userNameKana?.trim() ?? "");
  const hasGroup = normalizedGroupName !== "";
  const hasUser = normalizedUserName !== "";

  if (!hasGroup && !hasUser) {
    return { kind: "none" };
  }

  const groupPattern = hasGroup ? normalizedGroupName : null;
  // 担当者はカナ（kanaName）で照合する。カナが抽出できなかった場合は一致なしとする。
  const userKanaPattern = normalizedUserKana !== "" ? normalizedUserKana : null;
  const scopedGroupList = getScopedGroupList(entity.groupList, rootGroupId);

  const filterUsersByKana = (groupIds: Set<number>): UserEntity[] =>
    userKanaPattern
      ? entity.userList.filter(
          (u) => u.enable && groupIds.has(u.groupId) && matchesKanaName(u.kanaName, userKanaPattern)
        )
      : [];

  // ケース1.3: groupName + userName → ユーザー検索（カナ照合）
  if (groupPattern && hasUser) {
    const descendantIds = getMatchedDescendantIds(groupPattern, scopedGroupList);
    return { kind: "users", items: filterUsersByKana(descendantIds) };
  }

  // ケース1.2: userNameのみ → スコープ内ユーザーをカナ照合
  if (hasUser) {
    const scopedGroupIds = new Set(scopedGroupList.map((g) => g.groupId));
    return { kind: "users", items: filterUsersByKana(scopedGroupIds) };
  }

  // ケース1.1: groupNameのみ → スコープ内のisRecept=trueグループから名前検索
  if (!groupPattern) {
    return { kind: "none" };
  }

  const groups = scopedGroupList.filter((g) => g.isRecept && matchesName(g.names, groupPattern));
  return { kind: "groups", items: groups };
};
